import { styled } from '@linaria/react';
import { colors, fonts, layout, semantic, shadow, space } from '../../theme/tokens';

export const Panel = styled.div<{
  $columnView?: boolean;
  $singleMonth?: boolean;
  $stacked?: boolean;
}>`
  position: relative;
  background: ${colors.white};
  border-radius: ${layout.borderRadiusLg};
  box-shadow: ${({ $stacked }) => ($stacked ? 'none' : shadow.lg)};
  padding: ${space[4]};
  box-sizing: border-box;
  width: ${({ $stacked }) => ($stacked ? '100%' : 'fit-content')};
  max-width: 100%;
  display: ${({ $stacked }) => ($stacked ? 'flex' : 'block')};
  flex-direction: column;
  flex: ${({ $stacked }) => ($stacked ? '1 1 auto' : '0 0 auto')};
  min-height: ${({ $stacked }) => ($stacked ? '0' : 'auto')};
  max-height: ${({ $stacked }) => ($stacked ? '100%' : 'none')};
`;

export const MonthsContainer = styled.div<{
  $columnView?: boolean;
  $singleMonth?: boolean;
  $stacked?: boolean;
}>`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${space[4]};
  overflow-y: ${({ $stacked }) => ($stacked ? 'auto' : 'visible')};
  flex: ${({ $stacked }) => ($stacked ? '1 1 auto' : '0 0 auto')};
  min-height: ${({ $stacked }) => ($stacked ? '0' : 'auto')};
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }

  @media (min-width: 660px) {
    grid-template-columns: ${({ $columnView, $singleMonth, $stacked }) =>
      $stacked || $columnView || $singleMonth ? '1fr' : 'auto auto'};
    gap: ${space[5]};
  }
`;

export const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.6);
  border-radius: ${layout.borderRadiusLg};
  font-family: ${fonts.body};
  font-size: ${fonts.size.xs};
  color: ${semantic.text.secondary};
`;

export const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: ${space[4]};
  margin-top: ${space[4]};
`;

export const StackedWeekdays = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
  padding-bottom: ${space[4]};
  margin-bottom: ${space[4]};
  border-bottom: 1px solid ${semantic.border.subtle};

  @media screen and (max-width: 600px) {
    grid-template-columns: repeat(7, minmax(0, 1fr));
  }
`;

export const StackedWeekdayLabel = styled.span`
  text-align: center;
  font-family: ${fonts.body};
  font-size: ${fonts.size.sm};
  font-weight: ${fonts.weight.semiBold};
  color: ${semantic.text.secondary};
  padding: ${space[1]} 0;
`;

export const LoadMoreButton = styled.button`
  align-self: center;
  background: ${semantic.bg.surfaceSubtle};
  border: none;
  border-radius: ${layout.borderRadiusFull};
  padding: ${space[3]} ${space[5]};
  margin-top: ${space[5]};
  cursor: pointer;
  font-family: ${fonts.body};
  font-size: ${fonts.size.md};
  font-weight: ${fonts.weight.semiBold};
  color: ${semantic.text.primary};
  transition: background 0.15s ease;

  &:hover {
    background: ${semantic.bg.surfaceStrong};
  }

  &:focus-visible {
    outline: 2px solid ${colors.primary};
    outline-offset: 2px;
  }
`;

export const ResetButton = styled.button`
  background: none;
  border: none;
  padding: ${space[2]} 0;
  cursor: pointer;
  font-family: ${fonts.body};
  font-size: ${fonts.size.md};
  font-weight: ${fonts.weight.medium};
  color: ${semantic.text.primary};

  &:hover {
    font-weight: ${fonts.weight.semiBold};
  }

  &:focus-visible {
    outline: 2px solid ${colors.primary};
    outline-offset: 2px;
  }

  &:disabled {
    color: ${semantic.text.tertiary};
    cursor: not-allowed;
  }
`;

export const ApplyButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${space[3]} ${space[5]};
  background: ${colors.primary};
  color: ${colors.white};
  border: none;
  border-radius: ${layout.borderRadiusFull};
  cursor: pointer;
  font-family: ${fonts.body};
  font-size: ${fonts.size.md};
  font-weight: ${fonts.weight.semiBold};
  transition: opacity 0.15s ease;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid ${colors.primary};
    outline-offset: 2px;
  }

  &:hover:not(:disabled) {
    opacity: 0.9;
  }
`;
